import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Trash2, Calendar, Activity, ArrowLeft, Search, AlertCircle, Calculator, LogIn, Loader2, ChevronRight } from 'lucide-react';
import { reportsService, SavedReport } from '../lib/reportsService';

const riskColors: Record<string, string> = {
  low: 'text-green-700 bg-green-100',
  moderate: 'text-yellow-700 bg-yellow-100',
  high: 'text-orange-700 bg-orange-100',
  critical: 'text-red-700 bg-red-100'
};

export default function SavedReportsPage() {
  const [reports, setReports] = useState<SavedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(true);

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    const isAuth = await reportsService.isAuthenticated();
    setIsAuthenticated(isAuth);
    if (!isAuth) { setLoading(false); return; }
    const { data, error: loadError } = await reportsService.getReports();
    if (loadError) setError(loadError.message);
    else setReports(data);
    setLoading(false);
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this report?')) return;
    setDeletingId(id);
    const { success, error: deleteError } = await reportsService.deleteReport(id);
    if (!success && deleteError) alert('Failed to delete: ' + deleteError.message);
    else setReports(reports.filter(r => r.id !== id));
    setDeletingId(null);
  };

  const filteredReports = reports.filter(r =>
    r.calculator_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.calculator_short_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.risk_level.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (!isAuthenticated && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100">
        <header className="bg-white/80 backdrop-blur-sm border-b border-blue-100 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-600 hover:text-gray-900"><ArrowLeft className="w-5 h-5" /></Link>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-600 rounded-lg shadow-md"><FileText className="w-5 h-5 text-white" /></div>
                <div><h1 className="text-xl font-bold text-gray-900">Saved Reports</h1></div>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4"><LogIn className="w-8 h-8 text-blue-600" /></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Login Required</h2>
            <p className="text-gray-600 mb-6">Please log in to view your saved reports.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/auth/login" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"><LogIn className="w-5 h-5" />Log In</Link>
              <Link to="/auth/register" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">Create Account</Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100 flex items-center justify-center">
        <div className="text-center"><Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" /><p className="text-gray-600">Loading reports...</p></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100">
      <header className="bg-white/80 backdrop-blur-sm border-b border-blue-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-600 hover:text-gray-900"><ArrowLeft className="w-5 h-5" /></Link>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-600 rounded-lg shadow-md"><FileText className="w-5 h-5 text-white" /></div>
              <div><h1 className="text-xl font-bold text-gray-900">Saved Reports</h1><p className="text-sm text-gray-600">{reports.length} saved report{reports.length !== 1 ? 's' : ''}</p></div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {reports.length > 0 && (
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input type="text" placeholder="Search reports..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900" />
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2"><AlertCircle className="w-5 h-5" />{error}</div>}

        {!error && filteredReports.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><FileText className="w-8 h-8 text-gray-400" /></div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{searchTerm ? 'No Matching Reports' : 'No Saved Reports Yet'}</h2>
            <p className="text-gray-600 mb-6">{searchTerm ? 'Try adjusting your search.' : 'Use calculators and save AI reports.'}</p>
            <Link to="/calculators" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"><Calculator className="w-5 h-5" />Go to Calculators</Link>
          </div>
        )}

        <div className="space-y-4">
          {filteredReports.map((report) => (
            <Link key={report.id} to={`/reports/${report.id}`} className="block bg-white rounded-xl shadow-md border border-gray-100 p-5 hover:shadow-lg hover:border-blue-200 transition-all group">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-700">{report.calculator_short_name}</h3>
                    <span className={`px-2 py-0.5 text-xs font-bold rounded-full capitalize ${riskColors[report.risk_level] || 'text-gray-700 bg-gray-100'}`}>{report.risk_level} Risk</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{report.calculator_name}</p>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><Activity className="w-4 h-4" />Score: <span className="font-semibold text-gray-700">{report.score}</span></span>
                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(report.created_at)}</span>
                  </div>
                  <p className="text-gray-600 text-sm mt-3 line-clamp-2">{report.interpretation}</p>
                </div>
                <div className="flex sm:flex-col items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1 text-blue-600 text-sm font-medium">View<ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
                  <button onClick={(e) => handleDelete(report.id, e)} disabled={deletingId === report.id} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50" title="Delete">
                    {deletingId === report.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
